import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import { WsClient } from "@/lib/ws-client";
import { useWsRefetch } from "@/hooks/useWsRefetch";

/**
 * Synthetic lane id for docs with no job type (mirrors api queue-manager's
 * UNCLASSIFIED_JOB_TYPE_ID). Not a real JobType — lane-scoped writes
 * (instructions) must not target it.
 */
export const UNCLASSIFIED_JOB_TYPE_ID = "_unclassified";

export interface ReconciledTotals {
  live: number;
  byState: { ready: number; busy: number; error: number; complete: number };
}

export interface AssignedOperator {
  operatorId: string;
  operatorName: string;
  documentCount: number;
}

/** WU-2.2 — an active standing instruction stamped onto a lane or step. */
export interface LaneInstruction {
  id: string;
  body: string;
  createdAt: string;
}

/**
 * R-162 §10.1 / WU-3A — a per-(step, jobType) roster entry. Present on a
 * `byJobType` row only when the caller has `supervisor.queue_monitor` (the
 * server omits the whole `operators` key otherwise — see the L3 contract note:
 * `operators === undefined` means "I can't monitor", `operators === []` means
 * "I can monitor, nobody is here").
 */
export interface JobTypeOperator {
  userId: string;
  displayName: string;
  /** Doc the operator is currently keying on this lane, or null when idle. */
  currentDocumentId: string | null;
  /** Display id of that doc, when the server resolved one. */
  currentDocDisplayId?: string | null;
  /** ISO timestamp the operator's live lock was acquired, or null when idle. */
  lockedSince: string | null;
}

/**
 * R-162 — one job-type lane inside a step's queue card. WU-3A added the
 * supervisor-gated `operators` roster and the always-present-when-blocked
 * `blockedReason`.
 */
export interface JobTypeQueueRow {
  jobTypeId: string;
  jobTypeName: string;
  /** R-216 — back-compat alias of `docs`. Retained until all consumers migrate. */
  count: number;
  /** R-216 — work-items ready to pick (unassigned). */
  readyWi: number;
  /** R-216 — work-items currently held by an operator (assigned). */
  busyWi: number;
  /** R-216 — total work-items (readyWi + busyWi). */
  totWi: number;
  /** R-216 — distinct batch count contributing to this lane. */
  batches: number;
  /** R-216 — total documents in this lane (== count). */
  docs: number;
  /** Supervisor roster (monitor-gated — absent when the caller can't monitor). */
  operators?: JobTypeOperator[];
  /** Set when this (step, jobType) lane has an active supervisor block. */
  blockedReason?: string;
  /**
   * DE-1545 F-046 — count of failed/retrying in-flight export docs on this lane
   * (export step only). Present and > 0 only when a delivery failed, so a stuck
   * export is loudly visible on the dashboard.
   */
  exportFailed?: number;
  /** WU-2.2 — active standing instruction scoped to this lane. */
  instruction?: LaneInstruction;
}

export interface QueueSummary {
  stepType: string;
  totalCount: number;
  byDocumentType: { classification: string; count: number }[];
  byJobType: JobTypeQueueRow[];
  /**
   * Operators currently holding live Redis locks at this step, aggregated by
   * userId. Source: `queue-manager.listQueues`. Optional on the wire so older
   * responses don't break old clients; newer responses always include it.
   */
  assignedOperators?: AssignedOperator[];
  /**
   * R-262 / Q-d — count of `awaiting_release` docs at this step (classify-hold
   * docs that are non-assignable until a supervisor releases the batch). Absent
   * on older API responses; treated as 0 when undefined. Surface as a muted
   * "held — awaiting release (N)" indicator on the step header — NOT clickable
   * to "get next".
   */
  awaitingRelease?: number;
  /** WU-2.2 — active step-wide standing instruction for this step. */
  stepInstruction?: LaneInstruction;
  /** SP3 §3 — present only on remittance step rows. */
  remittanceSummary?: { batchCount: number; transactionCount: number };
}

interface QueuesResponse {
  queues: QueueSummary[];
  /** R-216 — Σ lane docs across in-process steps; sidebar badge source. */
  documentsInProcess?: number;
  /** F-030 Phase 3 — reconciled 4-state totals from the API. */
  reconciled?: ReconciledTotals;
}

// R-182 — every WS event that mutates queue state triggers a targeted refetch.
// `queue_update` is the original; `queue_block` / `queue_unblock` (R-162
// supervisor blocks) and `document_advance` (a doc moved to the next step) were
// previously unwired, so the QueueDashboard counts only updated on the slow
// poll. With these subscribed, the poll is just a reconnect safety net (30s).
// DE-1545 F-048 — `batch_released` (supervisor release) and `export_status`
// (worker export complete/fail) also mutate the lifecycle a pill reflects but
// were previously unsubscribed, so a pill could read "pending"/"awaiting
// release" after the underlying work had advanced/exported. Subscribing them
// makes the pills refresh live after a release and after an export lands.
// WU-2.2 — `queue_instruction` (supervisor set/clear on a lane or step) also
// mutates what the dashboard should render (the instruction banner), so it's
// subscribed here alongside the other queue-mutating events.
const QUEUE_WS_EVENTS = [
  "queue_update",
  "queue_block",
  "queue_unblock",
  "document_advance",
  "batch_released",
  "export_status",
  "queue_instruction",
] as const;

export function useQueues(wsClient: WsClient | null) {
  const [queues, setQueues] = useState<QueueSummary[]>([]);
  const [reconciled, setReconciled] = useState<ReconciledTotals | null>(null);
  // R-182 — stale-while-revalidate. `isLoading` is one-way (true until the
  // first fetch resolves); `isRefreshing` tracks WS-/poll-driven background
  // refetches so the dashboard never blanks its cards on refresh.
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    // Do NOT clear `queues` and do NOT flip `isLoading` true on refetch.
    setIsRefreshing(true);
    setError(null);
    try {
      const data = await api.get<QueuesResponse>("/queues");
      setQueues(data.queues);
      setReconciled(data.reconciled ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load queues");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Subscribe to all queue-mutating WS events for auto-refetch.
  useWsRefetch(wsClient, QUEUE_WS_EVENTS, refetch);

  return { queues, reconciled, isLoading, isRefreshing, error, refetch };
}
